import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  ExclamationCircleFilled,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { StatusType } from '@/pages/Shipments/statusType';
import styles from './StatusTag.module.css';
import {
  buildStatusPillStyle,
  getCustomerLegStatusPillColors,
  getShipmentStatusPillColors,
  isCustomerLegTerminalStatus,
} from '@/pages/Shipments/statusTagColors';

interface StatusTagProps {
  type?: StatusType;
  status?: string;
  alert_status?: string[];
  className?: string;
  style?: React.CSSProperties;
  textClassName?: string;
  textStyle?: React.CSSProperties;
  truncate?: boolean;
  customerLegEnabled?: boolean;
}

const StatusTag: React.FC<StatusTagProps> = ({
  type = 4,
  status = '',
  alert_status = [],
  className,
  style,
  textClassName,
  textStyle,
  truncate = false,
  customerLegEnabled = false,
}) => {
  const filteredAlerts = (
    Array.isArray(alert_status) ? alert_status : []
  ).filter((s) => String(s).toLowerCase() !== String(status).toLowerCase());

  const customerLegColors = customerLegEnabled
    ? getCustomerLegStatusPillColors(status)
    : undefined;

  const renderIcon = (rawType: StatusType) => {
    const t = Number(rawType) as StatusType;
    const colors = customerLegColors ?? getShipmentStatusPillColors(t);
    const iconStyle = { color: colors.color, marginRight: 4, fontSize: 12 };

    if (customerLegColors) {
      return isCustomerLegTerminalStatus(status) &&
        String(status).trim().toUpperCase() === 'DELIVERED' ? (
        <CheckCircleOutlined style={iconStyle} />
      ) : null;
    }

    if (t === 0 || t === 4) {
      return <ExclamationCircleFilled style={iconStyle} />;
    }
    if (t === 1) {
      return <WarningOutlined style={iconStyle} />;
    }
    if (t === 2 || t === 3) {
      return <CheckCircleOutlined style={iconStyle} />;
    }
    return null;
  };

  const t = Number(type) as StatusType;
  const colors = customerLegColors ?? getShipmentStatusPillColors(t);

  return (
    <Tag
      className={className}
      style={buildStatusPillStyle(colors, {
        borderRadius: 8,
        fontSize: 10,
        fontWeight: 600,
        width: truncate ? '100%' : 'fit-content',
        minWidth: truncate ? 0 : undefined,
        maxWidth: truncate ? '100%' : undefined,
        overflow: truncate ? 'hidden' : undefined,
        display: 'inline-flex',
        alignItems: 'center',
        ...style,
      })}
    >
      {renderIcon(type)}
      <span
        className={textClassName}
        data-shipment-tooltip-line={truncate ? 'true' : undefined}
        // textStyle comes from the caller at runtime
        // eslint-disable-next-line react/forbid-dom-props
        style={{
          minWidth: truncate ? 0 : undefined,
          overflow: truncate ? 'hidden' : undefined,
          textOverflow: truncate ? 'ellipsis' : undefined,
          whiteSpace: truncate ? 'nowrap' : undefined,
          ...textStyle,
        }}
      >
        {status}
      </span>
      {filteredAlerts.length > 0 && (
        <Tooltip title={filteredAlerts.join(', ')}>
          <span className={styles.alertCount}>+{filteredAlerts.length}</span>
        </Tooltip>
      )}
    </Tag>
  );
};

export default StatusTag;
